import React, { useState, useEffect } from "react";
import { 
  FileText, 
  Navigation, 
  MapPin, 
  Clock, 
  Search, 
  Calendar, 
  Download,
  AlertTriangle,
  RefreshCw
} from "lucide-react";
import axios from "../api/axios";

export default function Report() {
  const [imei, setImei] = useState("");
  const [selectedDateStart, setSelectedDateStart] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDateEnd, setSelectedDateEnd] = useState(new Date().toISOString().split('T')[0]);
  const [stats, setStats] = useState({
    distance: { day: "0 km", month: "0 km", total: "0 km" },
    trips: { day: "0", month: "0", total: "0" },
    time: { day: "0h 0m", month: "0h 0m", total: "0h 0m" }
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Function to fetch stats
  const fetchStats = async () => {
    if (!imei) {
      setErrorMsg("Vui lòng nhập IMEI để truy xuất dữ liệu");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      // Format start and end date for the selected range (removing milliseconds)
      const start_date = `${selectedDateStart}T00:00:00Z`;
      const end_date = `${selectedDateEnd}T23:59:59Z`;

      const payload = {
        imei: imei.trim(),
        start_time: start_date,
        end_time: end_date,
        page: 1,
        page_size: 10
      };

      const response = await axios.post("/logs/report", payload);
      const data = response.data;

      if (!data) {
        throw new Error("Không nhận được dữ liệu từ hệ thống");
      }

      setStats({
        distance: {
          day: `${data.total_mileage_day?.toFixed(2) || 0} km`,
          month: `${data.total_mileage_month?.toFixed(2) || 0} km`,
          total: `${data.total_mileage?.toFixed(2) || 0} km`
        },
        trips: {
          day: `${data.total_trips_day || 0}`,
          month: `${data.total_trips_month || 0}`,
          total: `${data.total_trips || 0}`
        },
        time: {
          day: data.total_driving_time_day || "0h 0m",
          month: data.total_driving_time_month || "0h 0m",
          total: data.total_driving_time || "0h 0m"
        }
      });
    } catch (error) {
      setErrorMsg(error.response?.data?.detail || "Không thể tải dữ liệu báo cáo. Vui lòng kiểm tra lại IMEI.");
    } finally {
      setLoading(false);
    }
  };

  // useEffect to trigger fetchStats when filters change
  useEffect(() => {
    if (imei && selectedDateStart && selectedDateEnd) {
      const timer = setTimeout(() => {
        fetchStats();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedDateStart, selectedDateEnd]); 

  // Stat Card component
  const StatCard = ({ title, value, label, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col space-y-2 hover:shadow-md transition-all hover:-translate-y-1">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">{label}</p>
          <h3 className="text-2xl font-black text-gray-900 mt-1">{value}</h3>
        </div>
        <div className={`p-3 rounded-2xl ${color} bg-opacity-10 text-gray-900`}>
          <Icon size={24} className={color.replace('bg-', 'text-')} />
        </div>
      </div>
      <p className="text-[10px] text-gray-400 mt-2 font-bold italic">{title}</p>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-8 py-4 flex justify-between items-center shadow-sm z-10">
          <div className="flex items-center space-x-4">
            <div className="bg-indigo-600 p-2 rounded-xl text-white shadow-lg shadow-indigo-100">
              <FileText size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight">WEBCHECK REPORT</h2>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Hệ thống thống kê hành trình</p>
            </div>
          </div>
        </header>

        {/* Filter Bar */}
        <main className="flex-1 overflow-y-auto p-6 md:p-10">
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 mb-8 flex flex-wrap items-center gap-4">
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
              <input
                type="text"
                placeholder="Nhập IMEI thiết bị..."
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-bold transition-all"
                value={imei}
                onChange={(e) => setImei(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchStats()}
              />
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center space-x-2 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3">
                <Calendar size={18} className="text-indigo-600" />
                <input
                  type="date"
                  className="bg-transparent border-none text-sm focus:outline-none text-gray-700 font-bold"
                  value={selectedDateStart}
                  onChange={(e) => setSelectedDateStart(e.target.value)}
                />
              </div>
              <span className="text-gray-400 font-bold">đến</span>
              <div className="flex items-center space-x-2 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3">
                <Calendar size={18} className="text-indigo-600" />
                <input
                  type="date"
                  className="bg-transparent border-none text-sm focus:outline-none text-gray-700 font-bold"
                  value={selectedDateEnd}
                  onChange={(e) => setSelectedDateEnd(e.target.value)}
                />
              </div>
            </div>

            <button
              onClick={fetchStats}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-2xl font-black transition-all shadow-xl shadow-indigo-100 text-sm flex items-center space-x-2 active:scale-95 disabled:opacity-50"
            >
              {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
              <span>TRUY XUẤT</span>
            </button>
            <button className="flex items-center space-x-2 text-indigo-600 hover:bg-indigo-50 px-6 py-3 rounded-2xl border border-indigo-100 transition-all text-sm font-black active:scale-95">
              <Download size={16} />
              <span>XUẤT EXCEL</span>
            </button>
          </div>

          {/* Error Message */}
          {errorMsg && (
            <div className="mb-8 p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-center space-x-3 text-rose-600 font-bold animate-in fade-in slide-in-from-top-2">
              <AlertTriangle size={20} />
              <span className="text-sm">{errorMsg}</span>
            </div>
          )}

          {/* Report Sections */}
          <div className="max-w-8xl mx-auto space-y-12">
            {/* Section 1: Quãng đường */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <Navigation size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 tracking-tight">Thống kê Quãng đường</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Trong ngày hôm nay" value={stats.distance.day} label="Quãng đường ngày" icon={Navigation} color="bg-blue-500" />
                <StatCard title="Tháng hiện tại (Tháng 01/2026)" value={stats.distance.month} label="Quãng đường tháng" icon={Navigation} color="bg-indigo-500" />
                <StatCard title="Kể từ khi kích hoạt thiết bị" value={stats.distance.total} label="Tổng quãng đường" icon={Navigation} color="bg-purple-500" />
              </div>
            </section>

            {/* Section 2: Chuyến đi */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                  <MapPin size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 tracking-tight">Thống kê Chuyến đi</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Các chuyến thực hiện trong ngày" value={stats.trips.day} label="Số chuyến ngày" icon={MapPin} color="bg-green-500" />
                <StatCard title="Tổng chuyến trong tháng này" value={stats.trips.month} label="Số chuyến tháng" icon={MapPin} color="bg-teal-500" />
                <StatCard title="Tổng số chuyến từ trước đến nay" value={stats.trips.total} label="Tổng số chuyến đi" icon={MapPin} color="bg-emerald-500" />
              </div>
            </section>

            {/* Section 3: Thời gian lái */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                  <Clock size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 tracking-tight">Thống kê Thời gian lái xe</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Thời gian vận hành hôm nay" value={stats.time.day} label="Thời gian ngày" icon={Clock} color="bg-orange-500" />
                <StatCard title="Tổng thời gian vận hành tháng" value={stats.time.month} label="Thời gian tháng" icon={Clock} color="bg-red-500" />
                <StatCard title="Tổng tích lũy thời gian lái" value={stats.time.total} label="Tổng thời gian lái" icon={Clock} color="bg-rose-500" />
              </div>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 p-4 text-center text-gray-400 text-xs">
          Hệ thống giám sát hành trình WEBCHECK © 2026 - Bảo mật dữ liệu thiết bị đầu cuối
        </footer>
      </div>
    </div>
  );
}
